import { createAsyncThunk } from '@reduxjs/toolkit';
import fileDownload from 'js-file-download';
import { rm } from 'lockr';

import { updateBusinessProductStatuses } from 'pages/dashboard/components/Progress/thunks';
import { getActiveProductsTypeCodes } from 'pages/dashboard/components/Progress/selectors';
import { getMerchant } from 'pages/dashboard/components/MerchantServices/selectors';
import { fetchBankAccounts } from 'pages/dashboard/components/BankAccounts/thunks';
import { fetchBPSTrack } from 'pages/dashboard/components/Configuration/thunks';
import { fetchProfileByEmail, updateBusiness } from 'pages/Profile/thunks';
import { erpCreationWrapper, erpUpdateBusiness } from 'services/erp';
import {
  getApplicationStatuses,
  getCountries,
  getBusinessLegalEntityFormTypes,
} from 'pages/dashboard/selectors';
import { showResponseError } from 'components/notification/utils';
import { mcTrackBPSCreationWrapper } from 'services/mcTrackBPS';
import { verifyCompliancePerson } from 'services/persons';
import { getResponseErrorMessages } from 'utils/helpers';
import { ApplicationStatus } from 'entities/dashboard';
import { ComplianceStatuses } from 'common/compliance';
import { prysymCreateMerchant } from 'services/prysym';
import { complianceWrapper } from 'services/platformCompliance';
import { smartCollectCreationWrapper } from 'services/smartCollect';
import {
  getBusinessInfo,
  getIdTypes,
} from 'pages/dashboard/components/BusinessInformation/selectors';
import { getOwners } from 'pages/dashboard/components/Ownership/selectors';
import { searchTandCacceptLogs } from 'services/tandc';
import { getBusinessesId } from 'utils/authService';
import { RootState } from 'state/store';
import {
  addBusinessesRequests,
  searchBusinessesRequests,
  getBusinessesRequests,
} from 'services/businesses';
import {
  ApplicationStatuses,
  ProductStatuses,
  ProductTypeCodes,
} from 'entities/progress';

import { isMerchantValid } from './utils';
import { getProfile } from './selectors';

export const approveCompliance: any = createAsyncThunk(
  'compliance/approveCompliance',
  async (complianceReference: string, thunk) => {
    const { getState, dispatch } = thunk;

    try {
      rm('serverTimeout');

      const state = getState() as RootState;

      const { person, business } = getProfile(state);

      try {
        const result = await verifyCompliancePerson({
          businessesId: business.businessesId,
          createdBy: person.emailAddress,
        });

        if (!result) {
          const message =
            'Business owner must complete Microsoft Authentication flow to enable access to SMART-Hub and continue the payment workflow.';
          showResponseError(message, dispatch);
          return false;
        }
      } catch (e: any) {
        const message = getResponseErrorMessages(e.response, true);
        showResponseError(message, dispatch);
        return false;
      }

      const merchant = getMerchant(state);
      const activeProducts = getActiveProductsTypeCodes(state);

      await mcTrackBPSCreationWrapper({
        businessesId: business.businessesId,
        createdBy: person.emailAddress,
      });

      if (
        activeProducts.includes(ProductTypeCodes.services) &&
        isMerchantValid(merchant)
      ) {
        await prysymCreateMerchant(business.businessesId);
      }

      await complianceWrapper({
        businessesId: business.businessesId,
        modifiedBy: person.emailAddress,
        complianceReference,
      });

      await erpCreationWrapper({
        businessesId: business.businessesId,
        createdBy: person.emailAddress,
      });

      await smartCollectCreationWrapper(business.businessesId);

      const statuses: ApplicationStatus[] = getApplicationStatuses(
        getState() as RootState,
      );

      const status: ApplicationStatus | undefined = statuses.find(
        (s: ApplicationStatus) => s.typeCode === ApplicationStatuses.approved,
      );

      if (status) {
        await dispatch(
          updateBusiness({
            applicationStatusID: status.applicationStatusId,
            applicationStatusTypeCode: status.typeCode,
            complianceStatusID: ComplianceStatuses.approved,
            complianceUpdatedBy: person.emailAddress,
            complianceReference,
          }),
        );

        await erpUpdateBusiness({
          businessesId: business.businessesId,
          createdBy: person.emailAddress,
        });

        dispatch(updateBusinessProductStatuses(ProductStatuses.approved));

        dispatch(fetchProfileByEmail(person.emailAddress));
        dispatch(fetchBPSTrack());
        dispatch(fetchBankAccounts());
      }
    } catch (e: any) {
      const message = getResponseErrorMessages(e.response, true);
      showResponseError(message, dispatch);
      return Promise.reject(e);
    }
  },
);

export const declineCompliance: any = createAsyncThunk(
  'compliance/declineCompliance',
  async (complianceReference: string, thunk) => {
    rm('serverTimeout');
    const { getState, dispatch } = thunk;

    try {
      const { person, business } = getProfile(getState() as RootState);
      const statuses: ApplicationStatus[] = getApplicationStatuses(
        getState() as RootState,
      );

      const status: ApplicationStatus | undefined = statuses.find(
        (s: ApplicationStatus) => s.typeCode === ApplicationStatuses.declined,
      );

      if (status) {
        const fields = {
          applicationStatusID: status.applicationStatusId,
          applicationStatusTypeCode: status.typeCode,
          complianceStatusID: ComplianceStatuses.declined,
          complianceUpdatedBy: person.emailAddress,
          complianceReference,
        };

        await dispatch(updateBusiness(fields));

        if (business.complianceStatusID) {
          await erpUpdateBusiness({
            businessesId: business.businessesId,
            createdBy: person.emailAddress,
          });
        }

        dispatch(fetchProfileByEmail(person.emailAddress));
      }
    } catch (e: any) {
      const message = getResponseErrorMessages(e.response, true);
      showResponseError(message, dispatch);
      return Promise.reject(e);
    }
  },
);

export const suspendCompliance: any = createAsyncThunk(
  'compliance/suspendCompliance',
  async (complianceReference: string, thunk) => {
    const { getState, dispatch } = thunk;

    try {
      const { person, business } = getProfile(getState() as RootState);
      const statuses: ApplicationStatus[] = getApplicationStatuses(
        getState() as RootState,
      );

      const status: ApplicationStatus | undefined = statuses.find(
        (s: ApplicationStatus) => s.typeCode === ApplicationStatuses.suspended,
      );

      if (status) {
        await dispatch(
          updateBusiness({
            applicationStatusID: status.applicationStatusId,
            applicationStatusTypeCode: status.typeCode,
            complianceStatusID: ComplianceStatuses.pending,
            complianceUpdatedBy: person.emailAddress,
            complianceReference,
          }),
        );

        if (business.complianceStatusID) {
          await erpUpdateBusiness({
            businessesId: business.businessesId,
            createdBy: person.emailAddress,
          });
        }
      }
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const activateCompliance: any = createAsyncThunk(
  'compliance/activateCompliance',
  async (complianceReference: string, thunk) => {
    const { getState, dispatch } = thunk;
    try {
      const { person } = getProfile(getState() as RootState);
      const statuses: ApplicationStatus[] = getApplicationStatuses(
        getState() as RootState,
      );

      const status: ApplicationStatus | undefined = statuses.find(
        (s: ApplicationStatus) =>
          s.typeCode === ApplicationStatuses.pendingApproval,
      );

      if (status) {
        await dispatch(
          updateBusiness({
            applicationStatusID: status.applicationStatusId,
            applicationStatusTypeCode: status.typeCode,
            complianceStatusID: ComplianceStatuses.pending,
            complianceUpdatedBy: person.emailAddress,
            complianceReference,
          }),
        );
      }
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const fetchBusinessRequests: any = createAsyncThunk(
  'compliance/fetchBusinessRequests',
  async (_: void, thunk) => {
    try {
      const { getState } = thunk;
      const { business } = getProfile(getState() as RootState);

      const requests = await searchBusinessesRequests({
        businessesId: business.businessesId,
        isActive: true,
      });

      return requests;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

// File name format: "Compliance Report - {BusinessName} - {BusinessId}.pdf".
// The whole name is capped at 200 chars; only the business name is truncated
// so the id and extension are always preserved.
const MAX_REPORT_FILENAME_LENGTH = 200;

const buildComplianceReportFilename = (
  businessName: string,
  businessesId: number | string | null,
): string => {
  const prefix = 'Compliance Report - ';
  const suffix = ` - ${businessesId ?? ''}.pdf`;
  const available = MAX_REPORT_FILENAME_LENGTH - prefix.length - suffix.length;
  const name = (businessName || '').trim().slice(0, Math.max(0, available));

  return `${prefix}${name}${suffix}`;
};

export const downloadComplianceReport: any = createAsyncThunk(
  'compliance/downloadComplianceReport',
  async (_arg: void, thunk) => {
    const { getState, dispatch } = thunk;

    try {
      const state = getState() as RootState;
      const businessesId = getBusinessesId();
      const businessInfo = getBusinessInfo(state);
      const owners = getOwners(state);
      const countries = getCountries(state);
      const legalEntityFormTypes = getBusinessLegalEntityFormTypes(state);
      const { idTypes: businessIdTypes } = getIdTypes(state);

      const [tandCLogs, privacyLogs] = await Promise.all([
        searchTandCacceptLogs({
          businessesId,
          tandCversion: 'onboarding/terms-of-use/EN',
        }),
        searchTandCacceptLogs({
          businessesId,
          tandCversion: 'onboarding/privacy-policy/EN',
        }),
      ]);

      const sortByDate = (a, b) =>
        new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();

      const tandCLog = tandCLogs.sort(sortByDate)[0] ?? null;
      const privacyLog = privacyLogs.sort(sortByDate)[0] ?? null;

      const { generateComplianceReportBlob } = await import(
        './components/ComplianceReport'
      );

      const blob = await generateComplianceReportBlob({
        businessesId,
        businessInfo,
        owners,
        countries,
        legalEntityFormTypes,
        businessIdTypes,
        tandCLog,
        privacyLog,
        generatedAt: new Date().toISOString(),
      });

      const filename = buildComplianceReportFilename(
        businessInfo.businessLegalName,
        businessesId,
      );

      fileDownload(blob, filename);
    } catch (e: any) {
      const message =
        'Unable to generate the Compliance Report. Please try again.';
      showResponseError(message, dispatch);
      return Promise.reject(e);
    }
  },
);

export const sendMessage: any = createAsyncThunk(
  'compliance/sendMessage',
  async (message: string, thunk) => {
    const { getState } = thunk;

    try {
      const { person, business } = getProfile(getState() as RootState);

      const data = {
        businessesId: business.businessesId,
        subject: `${person.givenName1} ${person.surnameFirst}`,
        messageText: message,
        createdBy: person.emailAddress,
        isActive: true,
        isHidden: false,
      };

      const requestId: any = await addBusinessesRequests(data);
      const requests = await getBusinessesRequests(requestId);

      return requests?.[0] || null;
    } catch (e) {
      return Promise.reject(e);
    }
  },
);
